import { FC, ReactNode, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faFilters, faSearch } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Box,
  Button,
  Grid,
  Group,
  Modal,
  Stack,
  Tabs,
  Text,
  TextInput,
  useMantineTheme,
} from "@mantine/core";
import { useElementSize, useMediaQuery } from "@mantine/hooks";

import { ErrorBoundary } from "../AppCrashFallback";

import { BreadcrumbItem, Breadcrumbs } from "./Breadcrumbs";
import { NextPreviousButtons } from "./NextPreviousButtons";
import {
  HoverCopyButtonTextProps,
  TitleContainer,
  TitleRightIconProps,
} from "./TitleContainer";

import styles from "./PageShell.module.css";

interface SearchProps {
  placeholder: string;
  onChange: (value: string) => void;
}

export type TopSectionProps<TType = boolean> = TType extends true
  ? {
      breadcrumbs?: BreadcrumbItem[];
      hasNextPrevious?: TType;
      onNext: () => void;
      onPrevious: () => void;
      previousDisabled: boolean;
      nextDisabled: boolean;
      nextTooltip?: string;
      previousTooltip?: string;
      enableHotkeys?: boolean;
    }
  : {
      breadcrumbs?: BreadcrumbItem[];
      hasNextPrevious?: TType;
    };

export interface TabNavigationProps {
  routerBasedNavigation?: {
    onChange: (value: string | null) => void;
    value: string;
    pagePath?: string;
  };
  tabs: {
    label: string | ReactNode;
    value: string;
    component: ReactNode;
    icon?: IconProp;
    rightSection?: ReactNode | number | string;
    disabled?: boolean;
  }[];
  defaultTab?: string;
  keepMounted?: boolean;
}

export interface PageShellProps {
  /** The title of the page */
  title: string;
  /** The url to navigate to when the title is clicked */
  titleUrl?: string;
  /** The title to display in the browser tab */
  documentTitle?: string;
  titleContentRight?: ReactNode;
  searchProps?: SearchProps;
  topSectionProps?: TopSectionProps;
  tabNavigationProps?: TabNavigationProps;
  countBeforeTitle?: number;
  hoverCopyButtonTextProps?: HoverCopyButtonTextProps;
  titleRightIconProps?: TitleRightIconProps;
  leftSection?: ReactNode;
  rightSection?: ReactNode;
  bottomSection?: ReactNode;
  withBorder?: boolean;
  /** Resets the padding of the children to default (whatever mantine deems default) */
  noChildrenPadding?: boolean;
  /** Sets the padding of children to 0. noChildrenPadding take precedence */
  zeroChildrenPadding?: boolean;
  filterComponent?: ReactNode;
  children?: ReactNode;
}

/**
 *
 * @param title The title of the page
 * @param titleUrl The url to navigate to when the title is clicked
 * @param documentTitle The title to display in the browser tab
 * @param searchProps The props for the search bar
 * @param topSectionProps The props for the top section of the page
 * @param tabNavigationProps The props for the tab based navigation. If you're using React Router to navigate, you can define the routerBasedNavigation prop.
 * @param countBeforeTitle The number to display before the title
 * @param hoverCopyButtonTextProps The props for the hover copy button text
 * @param titleRightIconProps The props for the icon to display to the right of the title
 * @param leftSection !!SHOULD BE A REACT FRAGMENT!! The content to display to
 * the left of the page (to the right of the title). Should be a react fragment
 * with at least one element in it because it will be wrapped in a Group.
 * @param rightSection !!SHOULD BE A REACT FRAGMENT!! The content to display to
 * the right of the page (to the right of the search bar). Should be a react
 * fragment with at least one element in it because it will be wrapped in a
 * Group.
 * @param bottomSection !!SHOULD BE A REACT FRAGMENT!! The content to display to
 * the button (under the title). Should be a react fragment with at least one
 * element in it because it will be wrapped in a Group.
 * @param withBorder If true, there will be a border between the title section and the page content. Defaults to true.
 * @param filterComponent The component to display in the filter section, which will be on the left side of the page if the page is wide enough. If the page
 * is not wide enough, it will be put into a modal, and a "Filters" button will be added to the rightSection.
 * @returns PageShell
 */
export const PageShell: FC<PageShellProps> = ({
  title,
  titleUrl,
  documentTitle,
  titleContentRight,
  searchProps,
  topSectionProps,
  tabNavigationProps,
  countBeforeTitle,
  hoverCopyButtonTextProps,
  titleRightIconProps,
  leftSection,
  rightSection,
  bottomSection,
  withBorder = true,
  noChildrenPadding,
  zeroChildrenPadding,
  filterComponent,
  children,
}) => {
  const { breakpoints } = useMantineTheme();
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.sm})`);
  const { ref, height: headingHeight } = useElementSize();
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const isLargeScreen = useMediaQuery(`(min-width: ${breakpoints.lg})`, true);

  useEffect(() => {
    const handler = () => {
      if (modalOpen) {
        setModalOpen(false);
      }
    };
    window.addEventListener("resize", handler);
    return () => {
      window.removeEventListener("resize", handler);
    };
  }, [modalOpen, setModalOpen]);

  const childrenPaddingValue = useMemo(() => {
    if (noChildrenPadding) return undefined;
    else if (zeroChildrenPadding) return 0;
    return 24;
  }, [noChildrenPadding, zeroChildrenPadding]);

  return (
    <>
      <Helmet>
        <title>{documentTitle || title} | Semgrep</title>
      </Helmet>
      <Box bg="gray.0">
        {/* Header*/}
        <Tabs
          variant="pills"
          radius="xl"
          keepMounted={tabNavigationProps?.keepMounted || false}
          defaultValue={
            tabNavigationProps?.routerBasedNavigation?.value
              ? undefined
              : tabNavigationProps?.defaultTab
          }
          value={tabNavigationProps?.routerBasedNavigation?.value || undefined}
          onChange={
            tabNavigationProps?.routerBasedNavigation?.onChange || undefined
          }
          mih="100vh"
          color="dark"
          classNames={{
            tab: styles.tab,
            tabLabel: styles.tabLabel,
          }}
        >
          <div ref={ref}>
            {/* min height here exactly matches the org picker height */}
            <Stack
              justify="center"
              mih={58}
              px={isMobile ? "sm" : "md"}
              py="sm"
            >
              {/* Top section */}
              {topSectionProps && (
                <Group justify="space-between" align="center">
                  <>
                    {/* Breadcrumbs */}
                    {/* Always render containing div so the NextPrevious buttons will always be on the right */}
                    <div>
                      {topSectionProps.breadcrumbs && (
                        <Breadcrumbs items={topSectionProps.breadcrumbs} />
                      )}
                    </div>

                    {/* Next / Previous buttons */}
                    {topSectionProps.hasNextPrevious && (
                      <NextPreviousButtons
                        onNext={topSectionProps.onNext}
                        onPrevious={topSectionProps.onPrevious}
                        nextTooltip={topSectionProps.nextTooltip}
                        previousTooltip={topSectionProps.previousTooltip}
                        nextDisabled={topSectionProps.nextDisabled}
                        previousDisabled={topSectionProps.previousDisabled}
                        enableHotkeys={topSectionProps.enableHotkeys}
                      />
                    )}
                  </>
                </Group>
              )}
              <Group
                justify="space-between"
                w="100%"
                align="start"
                wrap="nowrap"
              >
                {/* Title group */}
                <Stack
                  gap={bottomSection ? "xs" : 0}
                  style={{ alignSelf: "center" }}
                >
                  <Group gap="md">
                    <TitleContainer
                      title={title}
                      url={titleUrl}
                      countBeforeTitle={countBeforeTitle}
                      titleRightIconProps={titleRightIconProps}
                      hoverCopyButtonTextProps={hoverCopyButtonTextProps}
                      titleContentRight={titleContentRight}
                    />
                    {/* Left section container */}
                    {tabNavigationProps && (
                      <Tabs.List>
                        {tabNavigationProps.tabs.map((tab) => (
                          <Tabs.Tab
                            key={tab.value}
                            value={tab.value}
                            // right section can be a number or string, so wrap
                            // it in a fragment
                            rightSection={
                              tab.rightSection === undefined ? undefined : (
                                <>{tab.rightSection}</>
                              )
                            }
                            leftSection={
                              tab.icon ? (
                                <FontAwesomeIcon icon={tab.icon} />
                              ) : undefined
                            }
                            disabled={tab.disabled ?? false}
                          >
                            <Text fw={500} fz="sm">
                              {tab.label}
                            </Text>
                          </Tabs.Tab>
                        ))}
                      </Tabs.List>
                    )}
                    <Group gap="md">{leftSection}</Group>
                  </Group>
                  {bottomSection && <Group gap="sm">{bottomSection}</Group>}
                </Stack>

                {/* Right section container */}
                <Group gap="md">
                  {searchProps && (
                    <TextInput
                      leftSection={<FontAwesomeIcon icon={faSearch} />}
                      placeholder={searchProps.placeholder}
                      onChange={(event) =>
                        searchProps.onChange(event.currentTarget.value)
                      }
                      className={styles.searchInput}
                    />
                  )}
                  <Group gap="sm" wrap="nowrap" align="center">
                    {filterComponent && !isLargeScreen && (
                      // classes on this button are for the findings tutorial
                      // do not remove them or move them without verifying that the tutorial still works
                      // and that the filters button is highlighted on steps 1 and 5
                      <Button
                        className="findings-tutorial-step-1 findings-tutorial-step-5"
                        leftSection={<FontAwesomeIcon icon={faFilters} />}
                        onClick={() => setModalOpen((open) => !open)}
                        size="compact-sm"
                      >
                        Filters
                      </Button>
                    )}
                    {rightSection}
                  </Group>
                </Group>
              </Group>
            </Stack>
          </div>
          {/* Content */}
          <Grid
            grow={filterComponent === undefined}
            mih={`calc(100vh - ${headingHeight + 1}px)`}
            gutter="md"
            style={{ overflow: "hidden" }}
            classNames={{
              root: withBorder ? styles.borderTop : undefined,
              inner: styles.gridInner,
            }}
          >
            {filterComponent && isLargeScreen && (
              <Grid.Col
                span={3}
                className={styles.borderRight}
                py="md"
                px={24}
                maw={320}
                mih={`calc(100vh - ${headingHeight + 1}px)`}
              >
                {filterComponent}
              </Grid.Col>
            )}
            <Grid.Col
              py={childrenPaddingValue}
              span={{ base: 12, md: 9 }}
              px={childrenPaddingValue}
              className={styles.contentWithoutFilter}
              maw="100%"
              mih={`calc(100vh - ${headingHeight + 1}px)`}
            >
              {tabNavigationProps && (
                <>
                  {tabNavigationProps.tabs.map((tab) => (
                    <Tabs.Panel
                      key={tab.value}
                      value={tab.value}
                      mih="100%"
                      h="100%"
                      pos="relative"
                    >
                      {/* tabs panels must fill their containers so children can use Virtuoso */}
                      {tab.component}
                    </Tabs.Panel>
                  ))}
                </>
              )}
              <ErrorBoundary>{children}</ErrorBoundary>
            </Grid.Col>
          </Grid>
        </Tabs>
        <Modal
          opened={modalOpen}
          onClose={() => setModalOpen(false)}
          keepMounted={false}
        >
          {filterComponent}
        </Modal>
      </Box>
    </>
  );
};
